<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- Hero. `mkt-hero-watermark` + the right-shifted chat-bubble watermark
                 matches the pattern other marketing pages use (Pricing → payments,
                 Security → lock, etc.) so the Contact hero feels like part of the
                 family. The text container needs position:relative + z-index:1 so
                 it sits above the watermark layer. -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 40px;">
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="forum" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <h1 class="text-h2 text-weight-bold q-mb-md" style="color:#0F172A;">Contact Us</h1>
                    <p class="text-subtitle1" style="max-width:640px;margin:0 auto;color:#475569;">
                        Sales questions, partnership inquiries, press, or general feedback -
                        send us a message and we'll respond within one business day.
                    </p>
                </div>
            </section>

            <!-- Contact Methods. Each card has its own v-intersection so it reveals
                 independently (with reveal-delay-N for a tasteful left-to-right stagger),
                 plus a hover lift via .contact-method-card so the cards feel tactile. -->
            <section style="padding:60px 0; background:#f5f7fa;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-lg justify-center">

                        <!-- Email - JS-assembled, click-to-reveal/copy. Static HTML
                             contains only the visible-but-broken "mail [at] ..." form
                             so scrapers reading raw HTML see nothing usable. The real
                             address only assembles in the DOM after the user clicks. -->
                        <div class="col-12 col-sm-6 col-md-4 reveal-fade-up reveal-delay-1" v-intersection="revealCfg">
                            <q-card class="full-height text-center q-pa-lg contact-method-card">
                                <q-icon name="email" size="48px" color="primary" class="q-mb-md" />
                                <div class="text-h6 text-weight-bold q-mb-sm">Email</div>
                                <div class="text-body2 text-grey-7 q-mb-md">
                                    Prefer email? Reveal our address and copy it to your clipboard.
                                </div>
                                <!-- Default state: human-readable obfuscation that bots won't
                                     parse as an email. dir="ltr" + spaced [at]/[dot] is enough
                                     to defeat regex-only scrapers; full address only appears
                                     in DOM after the user interacts. -->
                                <div v-if="!emailRevealed" class="q-mb-md" style="font-size:1rem;color:#0F172A;font-weight:600;">
                                    {{ emailObfuscated }}
                                </div>
                                <div v-else class="q-mb-md" style="font-size:1rem;color:#0F172A;font-weight:600;word-break:break-all;">
                                    {{ assembledEmail }}
                                </div>
                                <q-btn outline color="primary" no-caps
                                       :label="emailRevealed ? 'Copy to clipboard' : 'Reveal email'"
                                       :icon="emailRevealed ? 'content_copy' : 'visibility'"
                                       @click="onEmailButton" />
                            </q-card>
                        </div>

                        <!-- Documentation -->
                        <div class="col-12 col-sm-6 col-md-4 reveal-fade-up reveal-delay-2" v-intersection="revealCfg">
                            <q-card class="full-height text-center q-pa-lg contact-method-card">
                                <q-icon name="menu_book" size="48px" color="teal" class="q-mb-md" />
                                <div class="text-h6 text-weight-bold q-mb-sm">Documentation</div>
                                <div class="text-body2 text-grey-7 q-mb-md">
                                    API reference, integration guides, and SDK documentation.
                                </div>
                                <q-btn outline color="teal" no-caps
                                       label="Developer Docs"
                                       href="/Docs/IntegrationGuides"
                                       icon="code" />
                            </q-card>
                        </div>

                        <!-- Existing customer support -->
                        <div class="col-12 col-sm-6 col-md-4 reveal-fade-up reveal-delay-3" v-intersection="revealCfg">
                            <q-card class="full-height text-center q-pa-lg contact-method-card">
                                <q-icon name="support_agent" size="48px" color="orange" class="q-mb-md" />
                                <div class="text-h6 text-weight-bold q-mb-sm">Existing Customer?</div>
                                <div class="text-body2 text-grey-7 q-mb-md">
                                    Sign in to your dashboard for account-specific support.
                                </div>
                                <q-btn outline color="orange" no-caps
                                       label="Sign In"
                                       href="/Account/Login"
                                       icon="login" />
                            </q-card>
                        </div>

                    </div>
                </div>
            </section>

            <!-- Contact Form - primary CTA. Posts to the same endpoint Support.vue
                 uses (HomeController.SubmitContactForm → Hangfire → email send). -->
            <section class="contact-form-section reveal-scale" v-intersection="revealCfg">
                <div class="contact-bg-swoosh"></div>
                <div class="contact-bg-circle contact-bg-circle--1"></div>
                <div class="contact-bg-circle contact-bg-circle--2"></div>

                <div class="mkt-container" style="position:relative;z-index:1;">
                    <div class="row justify-center">
                        <div class="col-12 col-md-8 col-lg-6">
                            <div class="text-center q-mb-lg">
                                <div class="contact-icon-wrapper">
                                    <div class="contact-icon-ring"></div>
                                    <q-icon name="chat" size="56px" color="primary" />
                                </div>
                                <div class="text-h4 text-weight-bold" style="color:#2d3344;letter-spacing:-0.02em;">Send Us a Message</div>
                                <div class="text-subtitle1 q-mt-sm" style="color:#4a5067;max-width:420px;margin:0 auto;">
                                    Replies typically arrive within one business day.
                                </div>
                            </div>

                            <q-card class="contact-card q-pa-lg">

                                <div v-if="submitted" class="text-center q-pa-xl">
                                    <q-icon name="check_circle" size="64px" color="positive" class="q-mb-md" />
                                    <div class="text-h5 text-weight-bold q-mb-sm">Message Sent</div>
                                    <div class="text-body1 text-grey-7 q-mb-lg">
                                        Thanks - we'll be in touch as soon as possible.
                                    </div>
                                    <q-btn outline color="primary" label="Send Another Message" @click="resetForm" no-caps />
                                </div>

                                <template v-else>
                                    <div class="column q-gutter-md">
                                        <q-input v-model="form.name" outlined label="Name *" :rules="[v => !!v || 'Required']" autocomplete="name" />
                                        <q-input v-model="form.email" outlined label="Email *" type="email" :rules="[v => !!v || 'Required']" autocomplete="email" />
                                        <q-input v-model="form.company" outlined label="Company (optional)" autocomplete="organization" />
                                        <q-select v-model="form.topic" outlined label="Topic" :options="topicOptions" emit-value map-options />
                                        <q-input v-model="form.message" outlined label="Message *" type="textarea" rows="5"
                                                 :rules="[v => !!v || 'Required']" />

                                        <!-- Honeypot anti-spam: hidden from real users, bots
                                             that auto-fill every visible input will populate it.
                                             Filled honeypot → silent client-side reject. -->
                                        <input v-model="form.website" type="text" tabindex="-1" autocomplete="off"
                                               aria-hidden="true"
                                               style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0;" />

                                        <q-btn color="primary" label="Send Message" no-caps unelevated size="lg"
                                               :loading="sending" :disable="!isFormValid"
                                               style="border-radius:10px;"
                                               @click="submitForm" />
                                    </div>
                                </template>

                            </q-card>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Site footer (shared across marketing pages so the Contact
                 page picks up the same nav links + branding the rest of the
                 site shows). -->
            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<style>
.mkt-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
@media (max-width: 599px) { .mkt-hide-mobile { display: none !important; } }

.contact-form-section {
    background: linear-gradient(160deg, #eef3fa 0%, #f4f5f7 40%, #eef0f7 100%);
    padding: 20px 0 80px;
    position: relative;
    overflow: hidden;
}
.contact-bg-swoosh {
    position: absolute; top: -30%; right: -20%; width: 70%; height: 80%;
    background: radial-gradient(ellipse at center, rgba(99, 102, 241, 0.05) 0%, transparent 70%);
    border-radius: 50%; pointer-events: none;
}
.contact-bg-circle { position: absolute; border-radius: 50%; pointer-events: none; }
.contact-bg-circle--1 {
    bottom: -10%; left: -10%; width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.03) 0%, transparent 70%);
}
.contact-bg-circle--2 {
    top: 10%; left: 5%; width: 200px; height: 200px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.025) 0%, transparent 70%);
}
.contact-icon-wrapper {
    position: relative; display: inline-flex; align-items: center;
    justify-content: center; margin-bottom: 8px;
}
.contact-icon-ring {
    position: absolute; width: 100px; height: 100px; border-radius: 50%;
    border: 2px solid rgba(99, 102, 241, 0.12);
    animation: contact-ring-pulse 2.5s ease-in-out infinite;
}
@keyframes contact-ring-pulse {
    0%, 100% { transform: scale(1); opacity: 0.6; }
    50% { transform: scale(1.12); opacity: 0; }
}
.contact-card {
    border-radius: 16px !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04) !important;
    border: 1px solid rgba(0, 0, 0, 0.04) !important;
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.92) !important;
}

/* Contact-method cards (Email / Documentation / Existing Customer). Strong
   resting shadow so they pop off the grey section background, plus a hover
   lift that reads as "interactive." Transition timing matches the reveal
   curve so the lift on entry feels continuous with the scroll-in animation. */
.contact-method-card {
    border-radius: 16px !important;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.05) !important;
    border: 1px solid rgba(0, 0, 0, 0.04) !important;
    background: #ffffff !important;
    transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1),
                box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1) !important;
}
.contact-method-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06) !important;
}
</style>

<script>
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

// Email-address obfuscation strategy.
//
// The contact email lives nowhere in the static HTML, robots.txt-accessible
// JSON-LD, or any data-* attribute. It is assembled at runtime from these
// three string fragments, which a regex scraper cannot detect as an email
// (no `@`, no `.com`, no string contains both halves of the local part).
// The full address only enters the DOM when the user clicks "Reveal email"
// or "Copy to clipboard".
//
// This won't stop a determined bot that executes JS, but it shuts down the
// large majority of address-harvesting scrapers (which read raw HTML and
// regex-match `\S+@\S+\.\S+`). The contact form remains the primary path.
const EMAIL_LOCAL_A = 'm';
const EMAIL_LOCAL_B = 'ail';
const EMAIL_DOMAIN = ['esignlaunchpad', 'com'];

export default {
    name: 'ContactPage',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingFooter },
    data() {
        return {
            isAuthenticated: typeof _AUTHENTICATED !== 'undefined' && _AUTHENTICATED === true,
            mobileNav: false,
            currentYear: new Date().getFullYear(),
            submitted: false,
            sending: false,
            emailRevealed: false,
            // Visible-but-broken form for non-interacting users + bots. The
            // [at]/[dot] notation is human-readable but defeats naive regex
            // scrapers that look for literal '@' / '.com' adjacency.
            emailObfuscated: 'mail [at] esignlaunchpad [dot] com',
            form: {
                name: '',
                email: '',
                company: '',
                topic: 'general',
                message: '',
                // Honeypot - must remain empty. Bots that auto-fill all inputs
                // will populate this and we drop the submission silently.
                website: ''
            },
            topicOptions: [
                { label: 'General Question', value: 'general' },
                { label: 'Sales', value: 'sales' },
                { label: 'Partnerships', value: 'partnerships' },
                { label: 'Investor Relations', value: 'investors' },
                { label: 'Press / Media', value: 'press' },
                { label: 'Technical Support', value: 'technical' },
                { label: 'Billing & Subscription', value: 'billing' },
                { label: 'API & Integration', value: 'api' },
                { label: 'Feature Request', value: 'feature' },
                { label: 'Bug Report', value: 'bug' },
                { label: 'Enterprise', value: 'enterprise' }
            ]
        };
    },
    computed: {
        // Assembles the real address only when accessed (via the v-if branch
        // that renders after the user clicks). Keeps the address out of the
        // initial render tree for any scraper that reads server-rendered or
        // hydrated HTML without further interaction.
        assembledEmail() {
            return EMAIL_LOCAL_A + EMAIL_LOCAL_B + String.fromCharCode(64) + EMAIL_DOMAIN.join('.');
        },
        isFormValid() {
            return this.form.name.trim() && this.form.email.trim() && this.form.message.trim();
        }
    },
    methods: {
        onEmailButton() {
            if (!this.emailRevealed) {
                this.emailRevealed = true;
                return;
            }
            // Second click: copy the assembled address to the clipboard.
            var addr = this.assembledEmail;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(addr).then(() => {
                    this.$q.notify({ type: 'positive', message: 'Email address copied to clipboard.' });
                }).catch(() => {
                    this.$q.notify({ type: 'warning', message: 'Could not copy. Address: ' + addr });
                });
            } else {
                this.$q.notify({ type: 'info', message: addr });
            }
        },
        resetForm() {
            this.submitted = false;
            this.form.name = '';
            this.form.email = '';
            this.form.company = '';
            this.form.topic = 'general';
            this.form.message = '';
            this.form.website = '';
        },
        async submitForm() {
            if (!this.isFormValid) return;
            // Honeypot tripped → pretend we sent the message but discard it.
            // Showing the success state denies bots the failure-signal feedback
            // they'd use to retry with the field cleared.
            if (this.form.website && this.form.website.trim() !== '') {
                this.submitted = true;
                return;
            }
            this.sending = true;
            try {
                // Topic + Company aren't first-class fields on the email job
                // (no schema change here - just one row labeled "Message" on
                // the receiving end). So we prepend them as plain header
                // lines inside the message body itself, where the recipient
                // can see them clearly without needing a new template field.
                // Looking up the human-readable label so the email shows
                // "Investor Relations" instead of "investors".
                var topicLabel = (this.topicOptions.find(o => o.value === this.form.topic) || {}).label || this.form.topic;
                var bodyLines = [];
                bodyLines.push('Topic: ' + topicLabel);
                if (this.form.company && this.form.company.trim()) {
                    bodyLines.push('Company: ' + this.form.company.trim());
                }
                bodyLines.push('');
                bodyLines.push(this.form.message);
                var payload = {
                    name: this.form.name,
                    email: this.form.email,
                    topic: this.form.topic,
                    message: bodyLines.join('\n')
                };
                const r = await fetch('/api/support/contact', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!r.ok) throw new Error('Failed');
                this.submitted = true;
            } catch {
                this.$q.notify({ type: 'negative', message: 'Failed to send message. Please try again in a moment.' });
            } finally {
                this.sending = false;
            }
        }
    }
};
</script>
